import React, { useEffect, useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { IUserObject, IVote } from 'types'
import api from 'helpers/api'
import { validateSourceUrl } from 'helpers/utils'

interface VoteCellModalProps extends ModalProps {
  rowNumber: number
  votes: IVote[]
}

const VoteCellModal: React.FC<VoteCellModalProps> = ({ id, open, setOpen, rowNumber, votes }) => {
  const [users, setUsers] = useState<Partial<IUserObject>[]>([])
  const [tab, setTab] = useState<string>('positive')

  const anonymousName = 'Anonymous User'

  const getAllUsers = async (userIds: string[]) => {
    const allUsers: Partial<IUserObject>[] = []
    for (let i = 0; i < userIds.length; i++) {
      const userId = userIds[i]
      let userDetails = {
        data: {
          name: anonymousName,
          publicId: userId,
          firebaseUserId: userId,
          email: 'anonymous@anonymous.com',
          profilePicture: ''
        }
      }

      try {
        userDetails = await api.getUser(userId)
      } catch {
        /* no-op */
      }

      allUsers.push(userDetails.data)
    }
    setUsers(allUsers)
  }

  useEffect(() => {
    const userIds: string[] = []
    for (let i = 0; i < votes.length; i++) {
      const vote: IVote = votes[i]
      if (!userIds.includes(vote.firebaseUserId)) userIds.push(vote.firebaseUserId)
    }
    getAllUsers(userIds)
  }, [])

  const positiveVotes = votes.filter((vote: IVote) => vote.type).length
  const negativeVotes = votes.filter((vote: IVote) => !vote.type).length

  return (
    <Modal id={id} open={open} setOpen={setOpen} title={`View Votes on Row #${rowNumber + 1}`}>
      <div className="flex items-center">
        <div
          className={`flex items-center cursor-pointer p-2.5 border-b-2 ${tab === 'positive' ? 'border-b-blue' : 'border-b-divider'}`}
          onClick={() => setTab('positive')}
        >
          <div>👍</div>
          <div className="ml-[5px]">{positiveVotes}</div>
        </div>
        <div
          className={`flex items-center cursor-pointer p-2.5 border-b-2 ${tab === 'negative' ? 'border-b-blue' : 'border-b-divider'}`}
          onClick={() => setTab('negative')}
        >
          <div>👎</div>
          <div className="ml-[5px]">{negativeVotes}</div>
        </div>
      </div>

      <div className="mt-5">
        {tab === 'positive' && positiveVotes === 0 && <p>There have been no positive votes so far.</p>}
        {tab === 'negative' && negativeVotes === 0 && <p>There have been no negative votes so far.</p>}
        {((tab === 'positive' && positiveVotes > 0) || (tab === 'negative' && negativeVotes > 0)) && (
          <div>
            {users.length !== 0 &&
              votes
                .filter((vote: IVote) => (tab === 'positive' && vote.type) || (tab === 'negative' && !vote.type))
                .sort((voteA: IVote, voteB: IVote) =>
                  voteA.timestamp > voteB.timestamp ? 1 : voteA.timestamp < voteB.timestamp ? -1 : 0
                )
                .map((vote: IVote, voteNumber: number) => {
                  const user: Partial<IUserObject> | undefined = users.find(
                    (user: Partial<IUserObject>) => user.firebaseUserId === vote.firebaseUserId
                  )
                  return (
                    <div className="flex items-center mb-2.5" key={voteNumber}>
                      <img
                        className="object-cover rounded-full mr-2.5 w-15 h-15"
                        src={validateSourceUrl(user?.profilePicture)}
                        alt={`${user?.name || 'Anonymous'}`}
                      />

                      <div className="flex flex-col">
                        {user && user.name && user.name !== anonymousName ? (
                          <a
                            className="truncate font-bold"
                            href={`/profile/${user.firebaseUserId}/information`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {user.name}
                          </a>
                        ) : (
                          <div className="truncate font-bold">{anonymousName}</div>
                        )}
                        <div className="text-sm mt-[5px]">Voted on: {vote.timestamp.split('T')[0]}</div>
                      </div>
                      <div className="text-lg ml-auto">{vote.type ? <div>👍</div> : <div>👎</div>}</div>
                    </div>
                  )
                })}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default VoteCellModal
